"use client";

import React, { useMemo } from "react";
import { getColorFromDivergingScale, getColorFromSequentialScale } from "@/utils/colors";
import type { CellComponentProps } from "../DataTable.types";

/**
 * Where a percentage stops being unremarkable.
 *
 * Both bounds are **inclusive**: a value exactly at `critical` colours. Exclusive
 * would leave the round number — the one a human configured — as the only
 * uncoloured value on the scale, which is the opposite of useful.
 *
 * Both are optional and independent: a column may want only a critical line.
 * `critical` wins when a value satisfies both.
 *
 * Deliberately *numbers*, not a level. What counts as low is a question about the
 * device and its configuration — a battery told to hold a 30% reserve is in trouble
 * at 28% and fine at 28% if its reserve is 5% — so the caller decides and this cell
 * only compares. See the `thresholds` prop.
 */
export interface PercentThresholds {
  /** Warn at or below this value. */
  low?: number;
  /** Alarm at or below this value. Takes precedence over `low`. */
  critical?: number;
}

/** The level a value falls into, or `undefined` when it is unremarkable. */
export type PercentThresholdLevel = "low" | "critical";

/**
 * The treatment inks, not a fourth palette.
 *
 * `--color-feedback-{warning,error}-text` are the same tokens the badge, status and
 * staleness treatments use for the mark itself, so amber in a bar is the same amber
 * as amber in a pill. A bar-specific palette is how a design system stops being one.
 */
const THRESHOLD_FILL: Record<PercentThresholdLevel, string> = {
  low: "var(--color-feedback-warning-text)",
  critical: "var(--color-feedback-error-text)",
};

/**
 * Classify a percentage against its thresholds.
 *
 * Exported because three callers need the same answer and must not each derive it:
 * this cell (to colour the fill), its serializable twin (to keep the label and the
 * marker consistent with it), and the tests.
 */
export function percentThresholdLevel(
  percentage: number,
  thresholds: PercentThresholds | undefined
): PercentThresholdLevel | undefined {
  if (!thresholds) return undefined;
  if (thresholds.critical !== undefined && percentage <= thresholds.critical) return "critical";
  if (thresholds.low !== undefined && percentage <= thresholds.low) return "low";
  return undefined;
}

export interface PercentBarCellProps extends CellComponentProps {
  value: number;
  showLabel?: boolean;
  color?: string | "auto";
  backgroundColor?: string | "none";
  height?: number;
  segments?: number;
  segmentGap?: number;
  colorScale?: {
    type: "sequential" | "diverging";
    scheme?: string;
    center?: number;
  };
  /**
   * Recolours the **fill** when the value is at or below a bound.
   *
   * The design inventory's `MeterCell` asks for a "threshold recolor below 20%"; the
   * number is the caller's because 20% is not universally low. Absent — the default —
   * renders exactly what this cell rendered before thresholds existed.
   *
   * Three things it deliberately does not do:
   *
   * - **The track never recolours.** The track is the scale, and a scale that changes
   *   colour with the reading is no longer a scale.
   * - **It does not replace the label.** Colour is the scan and the number is the
   *   answer; colour is never the only signal, which is also the accessibility floor.
   * - **It does not clamp or convert.** The comparison runs on the same 0–100 value
   *   the bar draws.
   *
   * Wins over `color` and `colorScale`: a threshold is a statement about this
   * reading, and a gradient is a restatement of the value the bar already shows.
   */
  thresholds?: PercentThresholds;
}

/**
 * PercentBarCell
 *
 * Horizontal bar showing percentage (0-100%).
 * Perfect for battery charge, progress indicators, and utilization metrics.
 * Supports segmented "LED meter" style with optional gradient coloring.
 */
export const PercentBarCell = React.memo<PercentBarCellProps>(
  ({
    value,
    showLabel = true,
    color = "var(--color-viz-categorical-1)",
    backgroundColor = "var(--color-background-muted)",
    height = 20,
    segments,
    segmentGap = 2,
    colorScale,
    thresholds,
    context,
  }) => {
    // Clamp value to 0-100
    const percentage = useMemo(() => {
      return Math.max(0, Math.min(100, value));
    }, [value]);

    // Compared against the value the bar draws, not the raw one: a reading outside
    // 0–100 is a different problem, marked separately by the caller, and "the fill is
    // empty" is the honest thing for the colour to agree with.
    const level = useMemo(() => percentThresholdLevel(percentage, thresholds), [percentage, thresholds]);

    // Get the effective color
    const effectiveColor = useMemo(() => {
      // A threshold outranks both. It says something about this reading; a gradient
      // only restates the length of the bar.
      if (level) return THRESHOLD_FILL[level];
      if (color === "auto" && colorScale) {
        if (colorScale.type === "diverging" && colorScale.center !== undefined) {
          return getColorFromDivergingScale(value, 0, 100, colorScale.center, colorScale.scheme);
        }
        return getColorFromSequentialScale(value, 0, 100, colorScale.scheme);
      }
      return color;
    }, [color, colorScale, level, value]);

    // Loading state
    if (context.isLoading) {
      return (
        <div className="flex items-center gap-2" style={{ height }}>
          <div className="h-full w-full animate-pulse bg-border-default rounded" />
        </div>
      );
    }

    // Segmented rendering
    if (segments && segments > 0) {
      const totalGapWidth = segmentGap * (segments - 1);
      const segmentWidth = `calc((100% - ${totalGapWidth}px) / ${segments})`;
      const filledSegments = Math.ceil((percentage / 100) * segments);

      return (
        <div className="flex items-center gap-2 w-full" data-threshold={level}>
          <div className="flex gap-0.5 flex-1" style={{ height }}>
            {Array.from({ length: segments }).map((_, i) => {
              const isFilled = i < filledSegments;

              // Get color for this segment if using auto color
              let segmentColor = effectiveColor;
              // …unless a threshold applies, in which case every filled segment is the
              // treatment colour. A gradient across an alarming reading would say two
              // things at once.
              if (!level && color === "auto" && colorScale && isFilled) {
                const segmentPercent = ((i + 1) / segments) * 100;
                if (colorScale.type === "diverging" && colorScale.center !== undefined) {
                  segmentColor = getColorFromDivergingScale(
                    segmentPercent,
                    0,
                    100,
                    colorScale.center,
                    colorScale.scheme
                  );
                } else {
                  segmentColor = getColorFromSequentialScale(segmentPercent, 0, 100, colorScale.scheme);
                }
              }

              return (
                <div
                  key={i}
                  className="rounded-sm"
                  style={{
                    width: segmentWidth,
                    backgroundColor: isFilled
                      ? segmentColor
                      : backgroundColor === "none"
                        ? "transparent"
                        : backgroundColor,
                    marginRight: i < segments - 1 ? `${segmentGap}px` : 0,
                  }}
                />
              );
            })}
          </div>
          {showLabel && <span className="text-sm text-text-primary whitespace-nowrap">{percentage.toFixed(0)}%</span>}
        </div>
      );
    }

    // Standard bar rendering
    return (
      <div className="flex items-center gap-2 w-full" data-threshold={level}>
        <div
          className="relative flex-1 rounded-sm"
          style={{
            height,
            backgroundColor: backgroundColor === "none" ? "transparent" : backgroundColor,
          }}
        >
          <div
            className="absolute left-0 top-0 h-full rounded-sm transition-all duration-300"
            style={{
              width: `${percentage}%`,
              backgroundColor: effectiveColor,
            }}
          />
        </div>
        {showLabel && <span className="text-sm text-text-primary whitespace-nowrap">{percentage.toFixed(0)}%</span>}
      </div>
    );
  }
);

PercentBarCell.displayName = "PercentBarCell";
